import React from "react";
import { useTheme, Box, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLookup } from "@rentablo/api/apiv1";
import {
  HorizontalBarChart,
  buildChartData,
} from "@components/Chart/HorizontalBarChart";
import { HorizontalBarChartItem } from "@components/Chart/HorizontalBarChart/types";
import { TypographyWithInfoIcon } from "@rentablo/components";
import { useRecommendationDetails } from "../../RecommendationDetailsProvider";
import { TwoColLayout } from "./TwoColLayout";
import { NoDataBlock } from "./NoDataBlock";

const BAR_HEIGHT = 34;

export const PerformanceSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { investment, logos, names, quote } = useRecommendationDetails();

  const { data: lookupData } = useLookup(
    {
      tickerSymbol: quote?.tickerSymbol!,
      quoteProvider: "ariva",
      includeIndexes: false,
      interval: "year",
      intervalPosition: "current",
      inPercentFrom: "currentToNow",
    },
    {
      enabled: Boolean(quote?.tickerSymbol),
    }
  );

  const { data: investmentLookupData } = useLookup(
    {
      tickerSymbol: investment?.tickerSymbol!,
      quoteProvider: investment?.quote?.quoteProvider!,
      includeIndexes: false,
      interval: "year",
      intervalPosition: "current",
      inPercentFrom: "currentToNow",
    },
    {
      enabled:
        Boolean(investment?.tickerSymbol) &&
        Boolean(investment?.quote?.quoteProvider),
    }
  );

  const recommendationData = lookupData?.historicQuotes[quote?.tickerSymbol!];
  const investmentData =
    investmentLookupData?.historicQuotes[investment?.tickerSymbol!];

  if (!recommendationData && !investmentData) return null;

  const chartData = buildChartData(
    t,
    theme.palette.graphVisual["02"].light,
    theme.palette.graphVisual["02"].light,
    recommendationData
  );

  const investmentChartData = buildChartData(
    t,
    theme.palette.graphVisual["06"].light,
    theme.palette.graphVisual["06"].light,
    investmentData
  );

  if (!chartData?.length && !investmentChartData?.length) return null;

  const renderChart = (
    data?: HorizontalBarChartItem[],
    name?: string,
    logo?: React.ReactNode
  ) => {
    const hasData = data?.length;
    const text = t("recommender.compareView.stockPerformanceOf", {
      name,
    });

    return (
      <Box height={hasData ? "auto" : "100%"}>
        <Box display="flex" alignItems="center" gap={2} mb={hasData ? 5 : 0}>
          {logo}
          <TypographyWithInfoIcon
            tooltipText={text}
            variant="body1"
            truncate
            hideIcon
          >
            {text}
          </TypographyWithInfoIcon>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={data?.length ? "auto" : "100%"}
        >
          {hasData ? (
            <HorizontalBarChart
              data={data}
              textColor="gainLoss"
              alignPercentage="left"
            />
          ) : (
            <NoDataBlock />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <TwoColLayout
        component1={renderChart(
          investmentChartData,
          names.investment,
          logos.originalFund
        )}
        component2={renderChart(
          chartData,
          names.recommendation,
          logos.recommendation
        )}
      />
      <Divider sx={{ my: 8 }} />
    </>
  );
};
